import axios from 'axios';
import { apiBase } from './apiBase';

const BACKEND_URL = apiBase();
const API = `${BACKEND_URL}/api`;
const USER_STORAGE_KEY = 'neuroheal_user';
const TOKEN_STORAGE_KEY = 'neuroheal_token';
const CSRF_COOKIE_NAME = 'neuroheal_csrf_token';
const CSRF_STORAGE_KEY = 'neuroheal_csrf_token';
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// ---------------------------------------------------------------------------
// Cross-site detection.
//
// When the frontend and backend live on the SAME origin (e.g. the emergent
// preview or a same-domain production deploy), the browser will happily
// attach the HttpOnly `neuroheal_access_token` cookie on every request and
// we NEVER need to touch localStorage. That's the safest path (immune to
// XSS token theft).
//
// When frontend + backend are on different origins (neurohealhaven.com →
// *.emergent.host), browsers with 3rd-party-cookie blocking (Safari ITP,
// Brave, Firefox ETP-strict, Chrome "block 3rd-party cookies") drop the
// cookie. In that case we fall back to a Bearer token cached in
// localStorage — same as before this migration. The `Authorization: Bearer`
// header sails through those browsers uninterrupted.
// ---------------------------------------------------------------------------
const isCrossSiteBackend = () => {
  try {
    if (typeof window === 'undefined') return false;
    return new URL(BACKEND_URL).origin !== window.location.origin;
  } catch {
    // If BACKEND_URL is malformed we assume cross-site to be safe.
    return true;
  }
};

// Evaluated once at module load — the origin pair does not change at runtime.
const USE_BEARER_FALLBACK = isCrossSiteBackend();

// If we're operating same-origin now, purge any legacy Bearer token that a
// previous cross-site session may have left behind. That's the whole point
// of the P2 hardening: no JWT in JS-readable storage when we don't need it.
if (!USE_BEARER_FALLBACK && typeof localStorage !== 'undefined') {
  try { localStorage.removeItem(TOKEN_STORAGE_KEY); } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Token storage — writes/reads are no-ops on same-origin.
// ---------------------------------------------------------------------------
export const setAuthToken = (token) => {
  if (!USE_BEARER_FALLBACK) return; // cookie-only mode: refuse to persist JWT
  if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
};

export const getAuthToken = () => {
  if (!USE_BEARER_FALLBACK) return null;
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

export const clearAuthToken = () => {
  // Always clear — cheap and idempotent.
  try { localStorage.removeItem(TOKEN_STORAGE_KEY); } catch { /* ignore */ }
};

// Exposed for tests / diagnostics.
export const isBearerFallbackActive = () => USE_BEARER_FALLBACK;

// CSRF token: only meaningful when the browser is actually sending the cookie.
// Stored in localStorage so cross-site frontends can read it (cookies set by
// the API domain are not visible via document.cookie on another origin).
export const setCsrfToken = (token) => {
  if (token) localStorage.setItem(CSRF_STORAGE_KEY, token);
};

export const clearCsrfToken = () => {
  localStorage.removeItem(CSRF_STORAGE_KEY);
};

export const getCsrfToken = () => {
  const fromLs = localStorage.getItem(CSRF_STORAGE_KEY);
  if (fromLs) return fromLs;
  const prefix = `${CSRF_COOKIE_NAME}=`;
  const cookie = document.cookie.split('; ').find((c) => c.startsWith(prefix));
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : undefined;
};

export const csrfHeaders = () => {
  const token = getCsrfToken();
  return token ? { 'X-CSRF-Token': token } : {};
};

// Headers helper for raw `fetch()` callers — mirrors what the axios
// interceptor does (Authorization + CSRF + Content-Type).
// Pass mutating=true on POST/PUT/PATCH/DELETE so CSRF header is included.
export const authFetchHeaders = (mutating = false) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (mutating) {
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  return headers;
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Authorization + CSRF headers on every request.
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (UNSAFE_METHODS.has((config.method || 'get').toLowerCase())) {
    const csrf = getCsrfToken();
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

// Auto-cache fresh tokens that come back in /auth/* responses.
// On 401: wipe local auth state so route guards redirect cleanly.
api.interceptors.response.use(
  (response) => {
    const incomingCsrf = response?.data?.csrf_token;
    if (typeof incomingCsrf === 'string' && incomingCsrf.length > 0) {
      setCsrfToken(incomingCsrf);
    }
    const incomingToken = response?.data?.token;
    if (typeof incomingToken === 'string' && incomingToken.length > 0) {
      setAuthToken(incomingToken);
    }
    return response;
  },
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem(USER_STORAGE_KEY);
      clearAuthToken();
      clearCsrfToken();
    }
    return Promise.reject(error);
  }
);

export default api;
